import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, FallbackScreen, MockAuthProvider } from "./authn";
import { environment, type EnvironmentSchema } from "./environment";
import { setAccessToken, setTokenProvider, clearTokenProvider } from "./api";
import { ThreadView } from "./thread";
import { ParamsProvider } from "@datapay-by-datacom/design-system";
import { HomeScreen } from "./home";
import {
  OrgSourcesScreen,
  OrgListScreen,
  OrgUserRolesScreen,
  OrgInvitesScreen,
  OrgLayout,
  OrgCreateScreen,
  OrgSettingsScreen,
  OrgAgentsScreen,
} from "./org";
import { TokenInvitationProvider } from "./org/providers";
import { ErrorBoundary, RouterErrorBoundary } from "./shared/components";
import { NotificationsScreen } from "./notifications";
import { ThreadExportScreen } from "./reports";
import { SettingsScreen } from "./settings";
import { AnnouncementsScreen } from "./announcements";
import {
  AgentCatalogLayout,
  AgenticPlatformScreen,
  AgentCatalogSection,
  CapabilityCatalogSection,
} from "./agent-catalog";
import {
  ShadowRunsLayout,
  DashboardScreen as ShadowRunsDashboard,
  TriggerRunScreen,
  HistoricalTrendScreen,
  ProgressMonitorScreen,
  ShadowRunDetailScreen,
  PacketListScreen,
  PacketDetailScreen,
  GraphExplorerScreen,
  SankeyScreen,
} from "./shadow-runs";
import {
  PlatformAnalyticsLayout,
  PlatformAnalyticsDashboard,
} from "./platform-analytics";
import {
  EvalStewardLayout,
  FeedbackReviewScreen,
  FeedbackDetailScreen,
} from "./eval-steward";
import {
  GroupAnalyticsScreen,
  GroupDetailScreen,
  UserGroupsScreen,
  UserManagementLayout,
  UserManagementScreen,
} from "./user-management";
import { WorkPatternImportScreen } from "./work-patterns";
import {
  SplitCostingImportLayout,
  SplitCostingUploadExperienceRoute,
  SplitCostingValidateExperienceRoute,
  SplitCostingResultExperienceRoute,
} from "./split-costing-import";
import { ComponentMatchingImportLayout } from "./component-matching-import";
import {
  OnboardingLayout,
  CompanyConfigLayout,
  BulkCreateCompanyExperienceLayout,
  BulkCreateCompanyReviewRoute,
  CompanyConfigCatalogueRoute,
  CompanyConfigVersionsRoute,
  LegacyOnboardingRouteRedirect,
  MaintainCompanyConfigRoute,
  RetireCompanyConfigRoute,
  BulkCreateCompanyScreen,
  BulkCreateCompanyResultsScreen,
} from "./onboarding";
import {
  PayComponentImportLayout,
  ErrorsExperienceRoute,
  MapColumnsExperienceRoute,
  ResultsExperienceRoute,
  ReviewExperienceRoute,
  UploadExperienceRoute,
} from "./pay-component-import";
import { ClientPayNormalisationLayout } from "./client-pay-normalisation";
import { FeatureFlagProvider } from "./feature-flags";
import { applyBrandConfig, installBrandDebugUtils } from "./brand-config";
import "./index.css";

// Apply brand configuration before React mounts to prevent flicker
applyBrandConfig();
installBrandDebugUtils();

// Normalise BASE_PATH for use as React Router's `basename`:
// "/" or "" -> undefined (default)
// "/123/" or "123" -> "/123"
const normaliseBasename = (basePath: string): string | undefined => {
  const trimmed = basePath.trim();
  if (!trimmed || trimmed === "/") {
    return undefined;
  }
  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return withLeadingSlash.replace(/\/+$/, "");
};


const createAppRouter = (basePath: string) =>
  createBrowserRouter(
    [
      {
        path: "/",
        element: <HomeScreen />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: "/:threadId",
        element: <ThreadView />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: "/notifications",
        element: <NotificationsScreen />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: "/reports",
        element: <ThreadExportScreen />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: "/settings",
        element: <SettingsScreen />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: "/release-announcements",
        element: <AnnouncementsScreen />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: "/org",
        element: <OrgLayout />,
        errorElement: <RouterErrorBoundary />,
        children: [
          {
            path: "",
            element: <OrgListScreen />,
          },
          {
            path: "create/:category",
            element: <OrgCreateScreen />,
          },
          {
            path: ":category/:id",
            element: <OrgSourcesScreen />,
          },
          {
            path: ":category/:id/agents",
            element: <OrgAgentsScreen />,
          },
          {
            path: ":category/:id/members",
            element: <OrgUserRolesScreen />,
          },
          {
            path: ":category/:id/invitations",
            element: <OrgInvitesScreen />,
          },
          {
            path: ":category/:id/settings",
            element: <OrgSettingsScreen />,
          },
        ],
      },
      {
        path: "/shadow-runs",
        element: <ShadowRunsLayout />,
        errorElement: <RouterErrorBoundary />,
        children: [
          {
            path: "",
            element: <ShadowRunsDashboard />,
          },
          {
            path: "trigger",
            element: <TriggerRunScreen />,
          },
          {
            path: "history",
            element: <HistoricalTrendScreen />,
          },
          {
            path: "progress/:agentId/:workflowId",
            element: <ProgressMonitorScreen />,
          },
          {
            path: ":batchId",
            element: <ShadowRunDetailScreen />,
          },
          {
            path: ":batchId/packets",
            element: <PacketListScreen />,
          },
          {
            path: ":batchId/packets/:packetId",
            element: <PacketDetailScreen />,
          },
          {
            path: ":batchId/packets/:packetId/graph",
            element: <GraphExplorerScreen />,
          },
          {
            path: ":batchId/sankey",
            element: <SankeyScreen />,
          },
        ],
      },
      {
        path: "/platform-analytics",
        element: <PlatformAnalyticsLayout />,
        errorElement: <RouterErrorBoundary />,
        children: [
          {
            path: "",
            element: <PlatformAnalyticsDashboard />,
          },
        ],
      },
      {
        path: "/agentic-platform",
        element: <AgentCatalogLayout />,
        errorElement: <RouterErrorBoundary />,
        children: [
          {
            element: <AgenticPlatformScreen />,
            children: [
              {
                index: true,
                element: <Navigate to="agent-catalog" replace />,
              },
              {
                path: "agent-catalog",
                element: <AgentCatalogSection />,
              },
              {
                path: "capability-catalog",
                element: <CapabilityCatalogSection />,
              },
            ],
          },
        ],
      },
      {
        path: "/eval-steward",
        element: <EvalStewardLayout />,
        errorElement: <RouterErrorBoundary />,
        children: [
          {
            path: "",
            element: <FeedbackReviewScreen />,
          },
          {
            path: ":workspaceId/:threadId/:assistantMessageIndex",
            element: <FeedbackDetailScreen />,
          },
        ],
      },
      {
        path: "/onboarding",
        element: <OnboardingLayout />,
        errorElement: <RouterErrorBoundary />,
        children: [
          {
            element: <CompanyConfigLayout />,
            children: [
              {
                path: "",
                element: <CompanyConfigCatalogueRoute />,
              },
              {
                path: "company-config-redesign/*",
                element: (
                  <LegacyOnboardingRouteRedirect
                    legacyBasePath="/onboarding/company-config-redesign"
                    canonicalBasePath="/onboarding"
                  />
                ),
              },
              {
                path: "new",
                element: <MaintainCompanyConfigRoute />,
              },
              {
                path: ":configCode",
                element: <MaintainCompanyConfigRoute />,
              },
              {
                path: ":configCode/versions",
                element: <CompanyConfigVersionsRoute />,
              },
              {
                path: ":configCode/versions/:version/retire",
                element: <RetireCompanyConfigRoute />,
              },
              {
                path: "bulk-create-company",
                element: <BulkCreateCompanyExperienceLayout />,
                children: [
                  {
                    path: "",
                    element: <BulkCreateCompanyScreen />,
                  },
                  {
                    path: "review",
                    element: <BulkCreateCompanyReviewRoute />,
                  },
                  {
                    path: "results",
                    element: <BulkCreateCompanyResultsScreen />,
                  },
                ],
              },
              {
                path: "bulk-create-redesign",
                element: (
                  <LegacyOnboardingRouteRedirect
                    legacyBasePath="/onboarding/bulk-create-redesign"
                    canonicalBasePath="/onboarding/bulk-create-company"
                  />
                ),
              },
              {
                path: "bulk-create-redesign/*",
                element: (
                  <LegacyOnboardingRouteRedirect
                    legacyBasePath="/onboarding/bulk-create-redesign"
                    canonicalBasePath="/onboarding/bulk-create-company"
                  />
                ),
              },
            ],
          },
          {
            path: "pay-component-import",
            element: <PayComponentImportLayout />,
            children: [
              {
                path: "",
                element: <UploadExperienceRoute />,
              },
              {
                path: "map",
                element: <MapColumnsExperienceRoute />,
              },
              {
                path: "review",
                element: <ReviewExperienceRoute />,
              },
              {
                path: "errors",
                element: <ErrorsExperienceRoute />,
              },
              {
                path: "results",
                element: <ResultsExperienceRoute />,
              },
            ],
          },
          {
            path: "work-pattern-import",
            element: <WorkPatternImportScreen />,
          },
          {
            path: "split-costing-import",
            element: <SplitCostingImportLayout />,
            errorElement: <RouterErrorBoundary />,
            children: [
              {
                path: "",
                element: <SplitCostingUploadExperienceRoute />,
              },
              {
                path: "validate",
                element: <SplitCostingValidateExperienceRoute />,
              },
              {
                path: "result",
                element: <SplitCostingResultExperienceRoute />,
              },
            ],
          },
          {
            path: "client-pay-normalisation",
            element: <ClientPayNormalisationLayout />,
            errorElement: <RouterErrorBoundary />,
          },
          {
            path: "component-matching-import",
            element: <ComponentMatchingImportLayout />,
            errorElement: <RouterErrorBoundary />,
          },
        ],
      },
      {
        path: "/user-management",
        element: <UserManagementLayout />,
        errorElement: <RouterErrorBoundary />,
        children: [
          {
            path: "",
            element: <UserManagementScreen />,
          },
          {
            path: "groups",
            element: <UserGroupsScreen />,
          },
          {
            path: "groups/:groupId",
            element: <GroupDetailScreen />,
          },
          {
            path: "groups/:groupId/analytics",
            element: <GroupAnalyticsScreen />,
          },
        ],
      },
    ],
    {
      basename: normaliseBasename(basePath),
    },
  );

const queryClient = new QueryClient();

// eslint-disable-next-line react-refresh/only-export-components
const AuthenticatedContent = ({
  launchDarklyClientId,
  children,
}: {
  launchDarklyClientId: string;
  children: React.ReactNode;
}) => {
  if (!launchDarklyClientId) {
    return <>{children}</>;
  }

  return (
    <FeatureFlagProvider clientSideId={launchDarklyClientId}>
      {children}
    </FeatureFlagProvider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const MainApp = () => {
  const [env, setEnv] = useState<EnvironmentSchema | null>(null);

  useEffect(() => {
    environment().then(setEnv);
  }, []);

  if (!env) {
    return <FallbackScreen state="loading" />;
  }

  const router = createAppRouter(env.BASE_PATH);

  const authContent = (
    <AuthenticatedContent launchDarklyClientId={env.LAUNCHDARKLY_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </AuthenticatedContent>
  );

  return (
    <ErrorBoundary>
      <TokenInvitationProvider>
        <ParamsProvider>
          {env.AUTH0_MOCK ? (
            <MockAuthProvider
              fallback={FallbackScreen}
              onLogout={() => { clearTokenProvider(); queryClient.clear(); }}
              setAccessToken={setAccessToken}
              setTokenProvider={setTokenProvider}
            >
              {authContent}
            </MockAuthProvider>
          ) : (
            <AuthProvider
              domain={env.AUTH0_DOMAIN}
              clientId={env.AUTH0_CLIENT_ID}
              audience={env.AUTH0_AUDIENCE}
              scope={env.AUTH0_SCOPE}
              fallback={FallbackScreen}
              onLogout={() => { clearTokenProvider(); queryClient.clear(); }}
              setAccessToken={setAccessToken}
              setTokenProvider={setTokenProvider}
              navigate={(url) => router.navigate(url)}
              addProvisionerRole={env.DEV_MOCK_PROVISIONER}
            >
              {authContent}
            </AuthProvider>
          )}
        </ParamsProvider>
      </TokenInvitationProvider>
    </ErrorBoundary>
  );
};

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MainApp />
  </StrictMode>
);
